import { createContext, useContext } from 'react';

import type {
  Conversation,
  DeveloperConfirmationDecision,
  Message,
  OutgoingAttachment,
  ResumeDecision,
  ResumeOptions,
} from '../types';

export interface ChatContextValue {
  /** Only hosts that preserve exact Developer decisions and send failures may enable reply actions. */
  supportsDeveloperReplies?: boolean;
  conversations: Conversation[];
  activeConversationId: string | null;
  activeConversation: Conversation | null;
  isArchived: boolean;
  /** Fail-closed while authoritative detail is loading/error, and true for archived chats. */
  isConversationReadOnly: boolean;
  /** The open chat answered 404 — unshared under this viewer, or deleted. Nothing left to show. */
  isConversationGone: boolean;
  streamingConvId: string | null;
  messages: Message[];
  isStreaming: boolean;
  /** Active assistant message is paused on a HITL interrupt awaiting approve/edit/reject. */
  isAwaitingApproval: boolean;
  isLoadingMessages: boolean;
  searchQuery: string;
  deletedActiveId: string | null;
  loadConversations: () => Promise<void>;
  setSearchQuery: (q: string) => void;
  selectConversation: (id: string) => Promise<void>;
  newChat: () => void;
  /** Reserve (and lock active) a conversation id so attachment uploads can be keyed to it before
   *  the first message is sent. Returns the existing active id when one is already set. */
  ensureConversationId: () => Promise<string | undefined>;
  deleteChat: (id: string) => Promise<void>;
  renameChat: (id: string, title: string) => Promise<void>;
  restoreChat: (id: string) => Promise<void>;
  togglePin: (id: string) => Promise<void>;
  sendMessage: (
    text: string,
    opts?: {
      developerReply?: import('../types.js').DeveloperReply;
      attachments?: OutgoingAttachment[];
      conversationId?: string | null;
      projectId?: string | null;
      /** Composer skill chips (Task 18) — forwarded to `streamChat` as `skill_ids`. */
      skillIds?: string[];
      /** Retry: answer this stored question again instead of sending the text. */
      regenerateOf?: string;
    },
  ) => Promise<void>;
  /**
   * Submit the user's decision on the currently pending HITL interrupt. `rememberSimilar` also
   * approves similar operations in this chat from now on.
   */
  submitResume: (
    decision: ResumeDecision,
    options?: ResumeOptions,
  ) => Promise<void>;
  /** Answer Developer's question about an inline `#developer` and follow the reply the answer starts. */
  decideDeveloperConfirmation: (
    messageId: string,
    decision: DeveloperConfirmationDecision,
  ) => Promise<void>;
  /** Stop the Developer run behind a watched message; only its starter sees the control (MEDA-1194). */
  stopDeveloperTask: (messageId: string) => Promise<void>;
  stopStreaming: () => void;
  /** Drop the most-recent cancelled assistant turn (and the user message
   *  that produced it) from local state and prefill the Composer with
   *  that user text so it can be tweaked and resent. */
  editLastCancelledTurn: () => void;
  markAssistantPending: () => void;
  streamRetryResponse: (
    convId: string,
    baselineCount: number,
    maxAttempts?: number,
    intervalMs?: number,
  ) => Promise<void>;
  clearDeletedActiveId: () => void;
  /** Re-read the open conversation from the server, so a turn finished out of band shows up at once. */
  syncMessages: () => void;
}

export const ChatContext = createContext<ChatContextValue | null>(null);

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error(
      'useChatContext must be used within a ChatContext.Provider',
    );
  }
  return ctx;
}
